// ── Welle Z1: Unterrichtszeiten der Lehrkraft (ersetzt die feste SP_ZEITEN-Tabelle) ──
// Quelle: GET /api/zeitraster (server/lib/zeitraster.js). Zwischengespeichert je Lehrkraft
// in localStorage; ohne Daten gilt das Standard-Raster (Hessen, 45-Minuten-Takt).
// zeiten[stunde - 1] = [Beginn, Ende] in Minuten seit Mitternacht — oder undefined, wenn die
// Schule diese Stunde nicht hat.
import { useEffect, useState } from 'react';
import { holeToken } from './sitzung';
import { apiGet } from './api';

export type RasterEintrag = { stunde: number; beginn: string; ende: string };
export type Zeitraster = { raster: RasterEintrag[]; standard: boolean };

export const MAX_STUNDE = 10;
export const STANDARD_RASTER: RasterEintrag[] = [
  ['07:45', '08:30'], ['08:30', '09:15'], ['09:30', '10:15'], ['10:15', '11:00'], ['11:20', '12:05'],
  ['12:05', '12:50'], ['12:50', '13:35'], ['13:35', '14:20'], ['14:35', '15:20'], ['15:20', '16:05'],
].map(([beginn, ende], i) => ({ stunde: i + 1, beginn, ende }));

export const zeitZuMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
export const fmtZeit = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Raster → Index nach Stunde (0 = 1. Stunde); fehlende Stunden = undefined */
export function zeitenAus(raster: RasterEintrag[]): Array<[number, number] | undefined> {
  const z: Array<[number, number] | undefined> = Array.from({ length: MAX_STUNDE }, () => undefined);
  for (const r of raster) if (r.stunde >= 1 && r.stunde <= MAX_STUNDE) z[r.stunde - 1] = [zeitZuMin(r.beginn), zeitZuMin(r.ende)];
  return z;
}

const EVENT = 'ld-zeitraster-update';
function lehrerId(): string | null {
  try {
    const teil = holeToken()?.split('.')[1];
    if (!teil) return null;
    const id = JSON.parse(window.atob(teil.replace(/-/g, '+').replace(/_/g, '/')))?.id;
    return id != null ? String(id) : null;
  } catch { return null; }
}
const key = () => { const id = lehrerId(); return id ? `ld-zeitraster-v1-${id}` : null; };
const STANDARD: Zeitraster = { raster: STANDARD_RASTER, standard: true };

function leseCache(): Zeitraster | null {
  const k = key(); if (!k) return null;
  try { const d = JSON.parse(localStorage.getItem(k) ?? 'null'); return Array.isArray(d?.raster) ? d : null; } catch { return null; }
}

/** Neuen Stand übernehmen (nach Laden, Speichern oder Zurücksetzen) und alle Anzeigen informieren */
export function setzeZeitrasterLokal(z: Zeitraster) {
  const k = key();
  if (k) { try { localStorage.setItem(k, JSON.stringify(z)); } catch { /* egal */ } }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: z }));
}

let laeuft: Promise<void> | null = null;
function vomServer() {
  if (!laeuft) {
    laeuft = apiGet<Zeitraster>('/api/zeitraster')
      .then(z => { if (Array.isArray(z?.raster)) setzeZeitrasterLokal(z); })
      .catch(() => { /* Zwischenspeicher bzw. Standard bleibt */ })
      .finally(() => { window.setTimeout(() => { laeuft = null; }, 2000); });
  }
  return laeuft;
}

/** Unterrichtszeiten der angemeldeten Lehrkraft (sofort aus Zwischenspeicher, dann vom Server) */
export function useZeitraster() {
  const [z, setZ] = useState<Zeitraster>(() => leseCache() ?? STANDARD);
  useEffect(() => {
    const neu = (e: Event) => setZ((e as CustomEvent<Zeitraster>).detail);
    window.addEventListener(EVENT, neu);
    void vomServer();
    return () => window.removeEventListener(EVENT, neu);
  }, []);
  return { ...z, zeiten: zeitenAus(z.raster) };
}
